<template>
  <div class="flex min-h-[80vh] items-center justify-center">
    <!-- Production Login Form -->
    <div class="w-full max-w-md p-8 bg-surface-container-lowest rounded-2xl shadow-lg border border-outline-variant/20">
      <div class="text-center mb-8">
        <img src="/logo.png" alt="RogueMixer" class="h-20 w-20 mx-auto mb-4 rounded-xl shadow-sm">
        <h1 class="font-headline-xl text-headline-xl mb-2 text-on-surface">
          Welcome, explorer.
        </h1>
        <p class="font-body-md text-body-md text-on-surface-variant">
          Sign into Rogue Rope Camp
        </p>
      </div>

      <!-- Google Login Button -->
      <button
        :disabled="loadingGoogle"
        class="w-full bg-surface-container-lowest text-on-surface border border-outline-variant hover:bg-surface-container-low font-semibold rounded-lg transition-all duration-150 ease-in-out mb-6 py-3 px-4 flex items-center justify-center shadow-sm active:scale-95"
        @click="handleGoogleLogin"
      >
        <svg v-if="!loadingGoogle" class="mr-2 h-5 w-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span v-if="loadingGoogle">Signing in...</span>
        <span v-else>Waltz in via Google</span>
      </button>

      <!-- Divider -->
      <div class="relative mb-6">
        <div class="absolute inset-0 flex items-center">
          <span class="w-full border-t border-outline-variant" />
        </div>
        <div class="relative flex justify-center text-xs uppercase">
          <span class="bg-surface-container-lowest px-2 text-on-surface-variant">Or slip in with</span>
        </div>
      </div>
      
      <!-- Magic Link Form -->
      <form class="space-y-4" @submit.prevent="handleLogin">
        <div>
          <label for="email" class="block text-label-md font-label-md mb-2 text-on-surface">
            Email
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            placeholder="you@example.com"
            class="w-full min-h-11 px-4 py-3 bg-surface-container-low border border-outline-variant rounded-lg text-on-surface placeholder:text-on-surface-variant focus:ring-2 focus:ring-secondary focus:border-secondary"
            required
          />
        </div>
        
        <button 
          type="submit" 
          :disabled="loading"
          class="w-full bg-primary text-on-primary py-4 px-6 rounded-lg font-label-md text-label-md shadow-md hover:bg-primary-container disabled:opacity-50 transition-all active:scale-95"
        >
          <span v-if="loading">Sending...</span>
          <span v-else>Send the key</span>
        </button>
      </form>

      <!-- Error/Success Messages -->
      <div v-if="authStore.error || message" class="mt-6 p-3 rounded-lg" :class="authStore.error ? 'bg-error-container text-on-error-container' : 'bg-primary-fixed text-on-primary-fixed'">
        {{ authStore.error || message }}
      </div>

      <div class="mt-8 text-center">
        <p class="text-sm text-on-surface-variant">
          Drop your email, we'll send a magic door.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'

const authStore = useAuthStore()

// Component is now production ready

const email = ref('')
const loading = ref(false)
const loadingGoogle = ref(false)
const message = ref('')

async function handleLogin() {
  loading.value = true
  message.value = ''

  try {
    await authStore.signInWithEmail(email.value)
    message.value = 'Check your email for the magic link.'
  } catch (error) {
    // Error is already set in store
  } finally {
    loading.value = false
  }
}

async function handleGoogleLogin() {
  loadingGoogle.value = true
  message.value = ''

  try {
    await authStore.signInWithGoogle()
    // User will be redirected to Google OAuth flow
  } catch (error) {
    // Error is already set in store
    loadingGoogle.value = false
  }
}
</script>
